// ============================================================================
// MÓDULO 7 - UNIDAD 4: DESCRIBING PEOPLE
// ============================================================================
// Nivel: B1-B2 (Upper-Intermediate)
// Gramática: Adjective Order (Opinion → Size → Shape → Age → Color)
// Vocabulario: Body parts, hair, appearance, age
// Slang: Ginger, bald eagle, silver fox, etc. (respetuoso)
// ============================================================================

export const M7_UNIT_4 = {
  id: "m7-u4",
  title: "Unit 4: Describing People",
  topic: "Master body vocabulary, adjective order, and describe people naturally with style and precision!",
  
  grammarImage: "/images/grammar/m7-u4-adjective-order.jpg",
  grammarText: `
    <h3>Describing People - Vocabulary & Adjective Order</h3>
    
    <div class="grammar-rule">
      <h4>Adjective Order in English</h4>
      <p>When using multiple adjectives, follow this strict order:</p>
      <ol>
        <li><strong>OPINION</strong> - beautiful, ugly, nice, gorgeous</li>
        <li><strong>SIZE</strong> - tall, short, big, small, medium</li>
        <li><strong>SHAPE</strong> - curly, straight, round, slim</li>
        <li><strong>AGE</strong> - young, old, middle-aged, elderly</li>
        <li><strong>COLOR</strong> - brown, blonde, black, red, gray</li>
      </ol>
      
      <div class="examples">
        <p><strong>Examples:</strong></p>
        <ul>
          <li>✅ A <strong>beautiful tall young</strong> woman (Opinion-Size-Age)</li>
          <li>✅ <strong>Gorgeous curly brown</strong> hair (Opinion-Shape-Color)</li>
          <li>✅ A <strong>handsome middle-aged</strong> man (Opinion-Age)</li>
          <li>❌ A brown curly gorgeous hair (WRONG ORDER!)</li>
        </ul>
      </div>
    </div>

    <div class="important-rule">
      <h4>⚠️ Important Rule</h4>
      <p>Don't use more than 2-3 adjectives in a row!</p>
      <p>✅ "Beautiful long blonde hair" (3 adjectives - OK)</p>
      <p>❌ "Beautiful gorgeous long wavy blonde shiny hair" (TOO MANY!)</p>
    </div>

    <div class="body-vocabulary">
      <h4>Essential Body Vocabulary</h4>
      <p><strong>Face:</strong> chin, eyebrow, forehead, nostrils, cheekbones</p>
      <p><strong>Hair:</strong> straight, wavy, curly, shoulder-length, ponytail, bald</p>
      <p><strong>Build:</strong> slim, athletic, heavyset, medium build</p>
      <p><strong>Height:</strong> tall, short, medium height</p>
      <p><strong>Age:</strong> young, middle-aged, elderly</p>
    </div>
  `,

  iconType: "user-circle",

  sets: [
    // ========================================================================
    // SET 1: FACE & HEAD VOCABULARY (WITH MATCH)
    // ========================================================================
    {
      id: "m7-u4-s1",
      title: "Set 1: Face & Head Features",
      questions: [
        {
          type: "match",
          q: "Match the face parts with their descriptions:",
          pairs: [
            { left: "Chin", right: "The bottom part of your face below your mouth" },
            { left: "Eyebrow", right: "The line of hair above your eye" },
            { left: "Forehead", right: "The part of your face above your eyes" },
            { left: "Nostrils", right: "The two openings in your nose" }
          ],
           a: "completed",
          successtip: "✓ FACE MAPPED!\nFun fact: Eyebrows prevent sweat from getting in your eyes! 💧👁️"
        },
        {
          type: "mc",
          q: "What are 'nostrils'?",
          options: [
            "The two openings in your nose",
            "The bridge of your nose",
            "The tip of your nose"
          ],
          a: "The two openings in your nose",
          successtip: "✓ CORRECT!\nRelated word: 'flare your nostrils' = expand them when angry or breathing hard! 😤"
        },
        {
          type: "input",
          q: "The area of your face above your eyes and below your hairline is called your ___.",
          a: "forehead",
          accept: { ignoreCase: true },
          successtip: "✓ PERFECT!\nIdiom: 'written all over your face/forehead' = obviously showing emotions! 😊😠"
        },
        {
          type: "mc",
          q: "'She raised her eyebrow' means:",
          options: [
            "She showed surprise or skepticism",
            "She combed her hair",
            "She closed her eyes"
          ],
          a: "She showed surprise or skepticism",
          successtip: "✓ BODY LANGUAGE!\nCultural: Raising one eyebrow = skeptical, both = surprised! 🤨😲"
        },
        {
          type: "input",
          q: "The bottom part of your face, below your mouth, is your ___.",
          a: "chin",
          accept: { ignoreCase: true },
          successtip: "✓ NICE!\nSlang: 'keep your chin up' = stay positive during difficult times! 💪😊"
        },
        {
          type: "mc",
          q: "'He has high cheekbones' describes:",
          options: [
            "The prominent bones under the eyes on the face",
            "His height",
            "His shoulders"
          ],
          a: "The prominent bones under the eyes on the face",
          successtip: "✓ FACIAL FEATURE!\nBeauty standard: High cheekbones are often considered attractive in modeling! 📸✨"
        },
        {
          type: "order",
          q: "Traduce: Ella tiene cejas gruesas y una frente ancha",
          a: "She has thick eyebrows and a broad forehead",
          successtip: "✓ DESCRIPTION!\nTrend: Thick/bold eyebrows have been fashionable since the 2010s! 💅"
        },
        {
          type: "mc",
          q: "Which is NOT a facial feature?",
          options: [
            "Ankle",
            "Chin",
            "Forehead"
          ],
          a: "Ankle",
          successtip: "✓ OBVIOUS!\nAnkle is on your foot/leg - totally different area! 🦶"
        },
        {
          type: "input",
          q: "The lines of hair above your eyes are called your ___. (plural)",
          a: "eyebrows",
          accept: { ignoreCase: true },
          successtip: "✓ PLURAL CORRECT!\nBeauty fact: Eyebrow shapes can change your whole face appearance! 🎭"
        },
        {
          type: "mc",
          q: "If someone has a 'strong chin', they have:",
          options: [
            "A prominent, well-defined chin",
            "A very small chin",
            "Multiple chins"
          ],
          a: "A prominent, well-defined chin",
          successtip: "✓ GOOD!\nOften associated with: Determination and confidence in face reading! 💪😎"
        }
      ]
    },

    // ========================================================================
    // SET 2: BODY & BUILD VOCABULARY (WITH MATCH)
    // ========================================================================
    {
      id: "m7-u4-s2",
      title: "Set 2: Body & Build",
      questions: [
        {
          type: "match",
          q: "Match body terms with descriptions:",
          pairs: [
            { left: "Slim", right: "Thin in an attractive way" },
            { left: "Athletic", right: "Muscular and fit from sports/exercise" },
            { left: "Heavyset", right: "Having a large, solid build" },
            { left: "Medium build", right: "Average size, not thin or heavy" }
          ],
           a: "completed",
          successtip: "✓ BUILD TYPES!\nCultural tip: 'Heavyset' is more polite than 'fat' or 'overweight'! 🤝"
        },
        {
          type: "mc",
          q: "'Athletic build' means:",
          options: [
            "Muscular and fit-looking",
            "Very thin",
            "Very tall"
          ],
          a: "Muscular and fit-looking",
          successtip: "✓ CORRECT!\nRelated: 'toned' = has visible muscle definition! 💪✨"
        },
        {
          type: "input",
          q: "A person who is thin in an attractive, healthy way can be described as ___.",
          a: "slim",
          accept: { ignoreCase: true, alts: ["slender"] },
          successtip: "✓ PERFECT!\nSynonym: 'slender' = similar meaning, slightly more elegant! 👗"
        },
        {
          type: "mc",
          q: "What's the most polite way to describe someone who is large/heavy?",
          options: [
            "Heavyset or large build",
            "Fat",
            "Huge"
          ],
          a: "Heavyset or large build",
          tip: "🤝 POLITE LANGUAGE!\n\nWhen describing size:\n\nPOLITE/NEUTRAL:\n✅ Heavyset\n✅ Large build\n✅ Plus-size\n✅ Big-boned\n\nIMPOLITE:\n❌ Fat (rude)\n❌ Huge (offensive)\n❌ Obese (medical term, not casual)\n\nAlways choose respectful language! 🎯",
          successtip: "✓ RESPECTFUL!\nModern trend: 'Body positive' language focuses on health, not appearance! ❤️"
        },
        {
          type: "mc",
          q: "'She has a medium build' means:",
          options: [
            "She's average size - not thin, not heavy",
            "She's very tall",
            "She's very muscular"
          ],
          a: "She's average size - not thin, not heavy",
          successtip: "✓ NEUTRAL DESCRIPTION!\n'Medium' is very safe and neutral for descriptions! ⚖️"
        },
        {
          type: "order",
          q: "Traduce: Él tiene una constitución atlética y es muy alto",
          a: "He has an athletic build and is very tall",
          successtip: "✓ DOUBLE DESCRIPTION!\nCombination: build (body type) + height (size) = complete picture! 📏💪"
        },
        {
          type: "input",
          q: "If someone is neither tall nor short, they are of ___ height. (medium / average)",
          a: "medium",
          accept: { ignoreCase: true, alts: ["average"] },
          successtip: "✓ NEUTRAL HEIGHT!\nStatistics: Medium height varies by country and gender! 📊🌍"
        },
        {
          type: "mc",
          q: "Which word describes a thin person in a NEGATIVE way?",
          options: [
            "Skinny (can sound negative)",
            "Slim",
            "Slender"
          ],
          a: "Skinny (can sound negative)",
          tip: "⚠️ WORD CONNOTATION!\n\nThin descriptions:\n\nPOSITIVE:\n✅ Slim - attractive thinness\n✅ Slender - elegant thinness\n✅ Lean - thin and muscular\n\nNEGATIVE/NEUTRAL:\n⚠️ Skinny - too thin (can be rude)\n⚠️ Bony - very thin (negative)\n\nContext and tone matter! Choose carefully! 🎯",
          successtip: "✓ NUANCE!\nAlternative: 'petite' for small, thin women (positive, especially in fashion)! 👗"
        },
        {
          type: "mc",
          q: "'Broad shoulders' means:",
          options: [
            "Wide, strong-looking shoulders",
            "Narrow shoulders",
            "Rounded shoulders"
          ],
          a: "Wide, strong-looking shoulders",
          successtip: "✓ STRONG FEATURE!\nIdiom: 'shoulder the responsibility' = take on a task (metaphor from strong shoulders)! 💪📦"
        },
        {
          type: "mc",
          q: "Complete naturally: 'She's quite ___ from all the running she does.'",
          options: [
            "athletic",
            "heavyset",
            "short"
          ],
          a: "athletic",
          successtip: "✓ LOGICAL!\nContext clue: Running → athletic build (cause and effect)! 🏃‍♀️💪"
        }
      ]
    },

    // ========================================================================
    // SET 3: HAIR DESCRIPTIONS (WITH MATCH + SLANG)
    // ========================================================================
    {
      id: "m7-u4-s3",
      title: "Set 3: Hair - Styles, Colors & Slang",
      questions: [
        {
          type: "match",
          q: "Match hair descriptions:",
          pairs: [
            { left: "Wavy", right: "Hair with gentle curves" },
            { left: "Curly", right: "Hair with tight spirals or ringlets" },
            { left: "Straight", right: "Hair with no curl or wave" },
            { left: "Bald", right: "Having no hair on the head" }
          ],
           a: "completed",
          successtip: "✓ HAIR TYPES!\nFun fact: Hair texture is genetic - determined by follicle shape! 🧬"
        },
        {
          type: "mc",
          q: "Someone with red/orange hair is informally called:",
          options: [
            "A ginger (UK slang, mostly harmless)",
            "A blondie",
            "A brunette"
          ],
          a: "A ginger (UK slang, mostly harmless)",
          successtip: "✓ SLANG ALERT!\n'Ginger' = British slang for redhead. Can be affectionate or teasing - tone matters! 🇬🇧🦊"
        },
        {
          type: "input",
          q: "Hair that reaches to the shoulders is called ___-length hair.",
          a: "shoulder",
          accept: { ignoreCase: true },
          successtip: "✓ LENGTH!\nOther lengths: chin-length, waist-length, back-length! 📏"
        },
        {
          type: "mc",
          q: "'She wears her hair in a ponytail' means:",
          options: [
            "Her hair is tied back in a bunch",
            "Her hair is very short",
            "Her hair is colored"
          ],
          a: "Her hair is tied back in a bunch",
          successtip: "✓ HAIRSTYLE!\nVariations: high ponytail, low ponytail, side ponytail! 🎀"
        },
        {
          type: "input",
          q: "A person with no hair on their head is described as ___.",
          a: "bald",
          accept: { ignoreCase: true },
          successtip: "✓ CORRECT!\nPlayful slang: 'bald eagle' or 'chrome dome' (friendly teasing among friends)! 🦅😄"
        },
        {
          type: "mc",
          q: "Natural hair colors include all EXCEPT:",
          options: [
            "Purple",
            "Blonde",
            "Black",
            "Red"
          ],
          a: "Purple",
          successtip: "✓ OBVIOUS!\nDyed hair: Purple, pink, blue = fashion/personal expression! 🎨💜"
        },
        {
          type: "order",
          q: "Traduce: Ella tiene cabello largo y ondulado",
          a: "She has long wavy hair",
          successtip: "✓ DESCRIPTION!\nNote: No article! 'Long wavy hair' (not 'a long wavy hair') ✅"
        },
        {
          type: "mc",
          q: "'Silver fox' is slang for:",
          options: [
            "An attractive older man with gray hair",
            "Someone with dyed silver hair",
            "A young person"
          ],
          a: "An attractive older man with gray hair",
          successtip: "✓ COMPLIMENT SLANG!\n'Silver fox' = very complimentary! Famous examples: George Clooney, Anderson Cooper! 🦊✨"
        },
        {
          type: "input",
          q: "Hair with tight spirals or ringlets is called ___ hair.",
          a: "curly",
          accept: { ignoreCase: true },
          successtip: "✓ TEXTURE!\nCurly hair types: loose curls, tight curls, coils, ringlets - all curly family! 🌀"
        },
        {
          type: "mc",
          q: "Which is the CORRECT natural order? 'She has ___ hair.'",
          options: [
            "beautiful long blonde",
            "long blonde beautiful",
            "blonde beautiful long"
          ],
          a: "beautiful long blonde",
          tip: "📐 ADJECTIVE ORDER!\n\nCorrect order:\n1. OPINION (beautiful)\n2. SIZE (long)\n3. COLOR (blonde)\n\n✅ 'Beautiful long blonde hair'\n\nThis follows: Opinion → Size → Color\n\nWrong orders sound unnatural to native speakers!\n\nThis is the FIRST time we see the adjective order rule in practice! 🎯",
          successtip: "✓ NATURAL ORDER!\nOpinion → Size → Color = the English way! 🇬🇧🇺🇸"
        }
      ]
    },

    // ========================================================================
    // SET 4: AGE & APPEARANCE (WITH MATCH)
    // ========================================================================
    {
      id: "m7-u4-s4",
      title: "Set 4: Age & General Appearance",
      questions: [
        {
          type: "match",
          q: "Match age-related terms:",
          pairs: [
            { left: "Young", right: "In the early part of life (teens-30s)" },
            { left: "Middle-aged", right: "Between young and old (40s-60s)" },
            { left: "Elderly", right: "Advanced in age (70+)" },
            { left: "Youthful", right: "Appearing young despite actual age" }
          ],
           a: "completed",
          successtip: "✓ AGE STAGES!\nPolite tip: 'Elderly' is more respectful than 'old' in formal contexts! 👴👵"
        },
        {
          type: "mc",
          q: "'Handsome' is typically used for:",
          options: [
            "Men",
            "Women",
            "Objects"
          ],
          a: "Men",
          successtip: "✓ GENDERED WORD!\nFor women: 'beautiful', 'pretty', 'gorgeous'. Though 'handsome' for women = strong beauty! 💃"
        },
        {
          type: "input",
          q: "A woman who is very attractive can be described as ___.",
          a: "beautiful",
          accept: { ignoreCase: true, alts: ["gorgeous", "pretty", "attractive"] },
          successtip: "✓ COMPLIMENT!\nScale: pretty < beautiful < gorgeous < stunning! 💕✨"
        },
        {
          type: "mc",
          q: "'She looks young for her age' means:",
          options: [
            "She appears younger than she actually is",
            "She acts immature",
            "She is very young"
          ],
          a: "She appears younger than she actually is",
          successtip: "✓ COMPLIMENT!\nRelated: 'age gracefully' = look good as you get older! ⏰✨"
        },
        {
          type: "mc",
          q: "Which is the most NEUTRAL/POLITE way to describe an older person?",
          options: [
            "Elderly or senior",
            "Old",
            "Ancient"
          ],
          a: "Elderly or senior",
          tip: "🤝 RESPECTFUL LANGUAGE!\n\nDescribing older people:\n\nPOLITE:\n✅ Elderly\n✅ Senior (citizen)\n✅ Older adult\n✅ In their golden years\n\nLESS POLITE:\n⚠️ Old (can sound rude)\n❌ Ancient (offensive)\n\nIn English-speaking cultures, being respectful about age is important! 🎯",
          successtip: "✓ RESPECTFUL!\n'Senior' is also positive: senior discount, senior center! 👴💚"
        },
        {
          type: "order",
          q: "Traduce: Él es un hombre de mediana edad muy apuesto",
          a: "He is a very handsome middle-aged man",
          successtip: "✓ DESCRIPTION!\nNotice: 'Very handsome' (opinion) comes before 'middle-aged' (age)! Opinion → Age! 📐"
        },
        {
          type: "input",
          q: "Someone between young and old, typically 40-60 years old, is ___-aged.",
          a: "middle",
          accept: { ignoreCase: true },
          successtip: "✓ CORRECT!\nNote the hyphen: 'middle-aged' (with hyphen) vs 'middle age' (noun, no hyphen)! ✍️"
        },
        {
          type: "mc",
          q: "'Good-looking' means:",
          options: [
            "Attractive (works for men and women)",
            "Looking at something well",
            "Wearing glasses"
          ],
          a: "Attractive (works for men and women)",
          successtip: "✓ NEUTRAL TERM!\n'Good-looking' = safe, gender-neutral alternative to handsome/beautiful! 👥✨"
        },
        {
          type: "mc",
          q: "If someone is 'young at heart', they:",
          options: [
            "Act youthful despite their age",
            "Have heart problems",
            "Are literally young"
          ],
          a: "Act youthful despite their age",
          successtip: "✓ IDIOM!\nCompliment: 'Young at heart' = positive, means energetic and optimistic! 💚😊"
        },
        {
          type: "mc",
          q: "Correct adjective order: 'She's a ___ woman.'",
          options: [
            "beautiful young",
            "young beautiful",
            "woman beautiful young"
          ],
          a: "beautiful young",
          successtip: "✓ ORDER CORRECT!\nOpinion (beautiful) before Age (young) = natural English! 📐✨"
        }
      ]
    },

    // ========================================================================
    // SET 5: ADJECTIVE ORDER - INFILTRATED NATURALLY
    // ========================================================================
    {
      id: "m7-u4-s5",
      title: "Set 5: Describing Naturally - Adjective Combinations",
      questions: [
        {
          type: "mc",
          q: "Which sounds most NATURAL? 'She has ___ hair.'",
          options: [
            "beautiful long brown",
            "brown long beautiful",
            "long brown beautiful"
          ],
          a: "beautiful long brown",
          tip: "📐 ADJECTIVE ORDER RULE!\n\nNatural order:\n1. OPINION (beautiful)\n2. SIZE/LENGTH (long)\n3. COLOR (brown)\n\n✅ 'Beautiful long brown hair'\n\nWhy this matters:\nNative speakers follow this automatically!\nWrong order = sounds strange/foreign\n\nThis pattern is FIXED in English! 🎯",
          successtip: "✓ NATURAL FLOW!\nNative speakers learn this naturally as kids - now you know the secret! 🗝️✨"
        },
        {
          type: "input",
          q: "Put in correct order: 'He's a ___ man.' (tall / handsome)",
          a: "handsome tall",
          accept: { ignoreCase: true },
          successtip: "✓ ORDER MASTERED!\nOpinion (handsome) → Size (tall) = always this way! 📏"
        },
        {
          type: "mc",
          q: "'A gorgeous curly black hair' - is this correct?",
          options: [
            "Almost! Should be 'gorgeous curly black hair' (no 'a')",
            "Perfect as is",
            "Completely wrong"
          ],
          a: "Almost! Should be 'gorgeous curly black hair' (no 'a')",
          tip: "⚠️ HAIR = UNCOUNTABLE!\n\nCommon error:\n❌ 'A hair' (wrong for describing hairstyle)\n✅ 'Hair' (uncountable)\n\nExceptions:\n✅ 'A hair' = one single strand\n'I found a hair in my soup!'\n\nBUT when describing someone:\n✅ 'She has beautiful hair' (no 'a')\n❌ 'She has a beautiful hair'\n\nHair as a mass = uncountable! 🎯",
          successtip: "✓ GOOD CATCH!\nBonus: Adjective order was correct (opinion-shape-color)! The only issue was the article! 📐"
        },
        {
          type: "order",
          q: "Traduce: Ella es una mujer joven muy atractiva",
          a: "She is a very attractive young woman",
          successtip: "✓ PERFECT!\n'Very' intensifies 'attractive' (opinion), then comes 'young' (age) - natural order! 💯"
        },
        {
          type: "mc",
          q: "Choose the natural description: 'I met a ___ guy yesterday.'",
          options: [
            "nice tall",
            "tall nice",
            "guy nice tall"
          ],
          a: "nice tall",
          successtip: "✓ SOUNDS NATIVE!\nOpinion → Size is the most common combination in casual speech! 💬"
        },
        {
          type: "mc",
          q: "'Slim young athletic' or 'athletic young slim' - which is correct order?",
          options: [
            "Neither - should be 'athletic slim young' (Opinion-Shape-Age)",
            "Both are fine",
            "First one"
          ],
          a: "Neither - should be 'athletic slim young' (Opinion-Shape-Age)",
          tip: "🎯 TRICKY ORDER!\n\nLet's break down:\n• 'Athletic' = opinion (implies fit/good)\n• 'Slim' = shape\n• 'Young' = age\n\nCorrect order:\nOpinion → Shape → Age\n= Athletic slim young\n\nBUT! Too many adjectives!\nBetter: 'athletic young' OR 'slim young'\n\nRule: Don't use more than 2-3 adjectives! 🎯",
          successtip: "✓ COMPLEX!\nIn reality, natives would say just 'young and athletic' or 'slim and young' - simpler! 😊"
        },
        {
          type: "input",
          q: "Correct order: 'He has ___ eyes.' (beautiful / big / brown)",
          a: "beautiful big brown",
          accept: { ignoreCase: true },
          successtip: "✓ THREE ADJECTIVES!\nOpinion → Size → Color = memorize this sequence! 👁️✨"
        },
        {
          type: "mc",
          q: "Which is TOO MANY adjectives?",
          options: [
            "A beautiful gorgeous stunning amazing young woman",
            "A beautiful young woman",
            "A tall handsome man"
          ],
          a: "A beautiful gorgeous stunning amazing young woman",
          successtip: "✓ OVERLOAD!\nRule of thumb: Maximum 2-3 adjectives, or it sounds unnatural! 🚫📝"
        },
        {
          type: "mc",
          q: "'Short wavy red' or 'wavy short red' hair?",
          options: [
            "Short wavy red (Size-Shape-Color)",
            "Wavy short red",
            "Both sound natural"
          ],
          a: "Short wavy red (Size-Shape-Color)",
          successtip: "✓ CORRECT SEQUENCE!\nSize (short) → Shape (wavy) → Color (red) = the pattern! 🌈"
        },
        {
          type: "order",
          q: "Traduce: Él tiene ojos grandes y azules",
          a: "He has big blue eyes",
          successtip: "✓ SIMPLE & NATURAL!\nSize (big) → Color (blue) - just two adjectives = perfect! 👁️💙"
        }
      ]
    },

    // ========================================================================
    // SET 6: FULL ADJECTIVE ORDER PRACTICE
    // ========================================================================
    {
      id: "m7-u4-s6",
      title: "Set 6: Mastering Full Adjective Order",
      questions: [
        {
          type: "mc",
          q: "Complete the rule: Opinion → ___ → Shape → Age → Color",
          options: [
            "Size",
            "Material",
            "Origin"
          ],
          a: "Size",
          tip: "📐 FULL ADJECTIVE ORDER!\n\nComplete order:\n1. OPINION (beautiful, ugly)\n2. SIZE (big, tall, long)\n3. SHAPE (round, curly, slim)\n4. AGE (young, old)\n5. COLOR (red, blue, blonde)\n\nMemory trick: \"OSSAC\"\nOpinion-Size-Shape-Age-Color\n\nFor describing people, these 5 are key! 🎯",
          successtip: "✓ SEQUENCE!\nFull English adjective order has more categories, but for people these 5 are essential! 📚"
        },
        {
          type: "input",
          q: "Order these: young / gorgeous / tall → 'She's a ___ woman.'",
          a: "gorgeous tall young",
          accept: { ignoreCase: true },
          successtip: "✓ ALL THREE!\nOpinion (gorgeous) → Size (tall) → Age (young) = complete order! 🌟"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "Curly beautiful blonde hair",
            "Beautiful curly blonde hair",
            "Beautiful blonde curly hair"
          ],
          a: "Curly beautiful blonde hair",
          tip: "🚫 WRONG ORDER!\n\nWhy 'Curly beautiful blonde' is wrong:\n• Beautiful = OPINION (should be first!)\n• Curly = SHAPE (should be middle)\n• Blonde = COLOR (should be last)\n\nCorrect order:\n✅ Beautiful curly blonde (Opinion-Shape-Color)\n\nWhen opinion comes second or last, it sounds very strange to native speakers! 🎯",
          successtip: "✓ ERROR FOUND!\nOpinion must ALWAYS come first in the sequence! 🥇"
        },
        {
          type: "order",
          q: "Traduce: Él es un hombre alto y apuesto de mediana edad",
          a: "He is a handsome tall middle-aged man",
          successtip: "✓ COMPLEX!\nOpinion (handsome) → Size (tall) → Age (middle-aged) = three-adjective description! 👔"
        },
        {
          type: "mc",
          q: "'She has ___ hair.' Fill with THREE adjectives in correct order:",
          options: [
            "gorgeous long curly",
            "long gorgeous curly",
            "curly long gorgeous"
          ],
          a: "gorgeous long curly",
          successtip: "✓ TRIPLE ORDER!\nOpinion → Size → Shape = the pattern for hair descriptions! 💇‍♀️"
        },
        {
          type: "input",
          q: "Fix the order: 'brown beautiful eyes' → '___ eyes'",
          a: "beautiful brown",
          accept: { ignoreCase: true },
          successtip: "✓ CORRECTED!\nColor should never come before opinion - flip them! 👁️✨"
        },
        {
          type: "mc",
          q: "Maximum recommended adjectives in a row:",
          options: [
            "2-3 adjectives",
            "5-6 adjectives",
            "As many as possible"
          ],
          a: "2-3 adjectives",
          successtip: "✓ LESS IS MORE!\nNative speakers rarely use more than 3 - it sounds unnatural and overwhelming! 📝"
        },
        {
          type: "mc",
          q: "'Old tall slim' or 'tall slim old' or 'slim tall old'?",
          options: [
            "Tall slim old (Size-Shape-Age)",
            "Old tall slim",
            "Slim tall old"
          ],
          a: "Tall slim old (Size-Shape-Age)",
          tip: "📐 THREE-WAY COMPARISON!\n\nBreaking it down:\n• Tall = SIZE\n• Slim = SHAPE\n• Old = AGE\n\nCorrect order: Size → Shape → Age\n= Tall slim old\n\nBUT! Better to say:\n'Tall and slim, but getting old' (more natural)\n\nOr simplify: 'a slim elderly man'\n\nDon't force too many adjectives! 🎯",
          successtip: "✓ CORRECT BUT...\nIn reality, 'a tall slim elderly man' sounds better (using 'elderly' instead of 'old')! 👴"
        },
        {
          type: "order",
          q: "Traduce: Ella tiene cabello corto, rizado y rubio",
          a: "She has short curly blonde hair",
          successtip: "✓ NATURAL THREE!\nSize (short) → Shape (curly) → Color (blonde) = perfect sequence! 👱‍♀️"
        },
        {
          type: "mc",
          q: "Which sentence uses adjective order PERFECTLY?",
          options: [
            "She's a beautiful tall young woman with gorgeous long brown hair",
            "She's a tall beautiful young woman with long gorgeous brown hair",
            "She's a young tall beautiful woman with brown long gorgeous hair"
          ],
          a: "She's a beautiful tall young woman with gorgeous long brown hair",
          successtip: "✓ PERFECT TWICE!\nBoth phrases follow opinion → size/length → age/color = mastery! 🌟👑"
        }
      ]
    },

    // ========================================================================
    // SET 7: COMMON ERRORS & CORRECTIONS
    // ========================================================================
    {
      id: "m7-u4-s7",
      title: "Set 7: Avoiding Common Description Errors",
      questions: [
        {
          type: "mc",
          q: "Identify the error: 'He's a young handsome man.'",
          options: [
            "Should be 'handsome young' (opinion before age)",
            "No error",
            "'Young' should be 'younger'"
          ],
          a: "Should be 'handsome young' (opinion before age)",
          tip: "🚫 COMMON ERROR!\n\nMany learners put age first because they think chronologically:\n❌ 'Young handsome man'\n\nBut English always puts OPINION first:\n✅ 'Handsome young man'\n\nOpinion → Age (ALWAYS!)\n\nEven though we think 'he's young' first, we say opinion first in descriptions! 🎯",
          successtip: "✓ FIXED!\nThink: What's your OPINION first? → Then facts (size, age, color) 💭"
        },
        {
          type: "input",
          q: "Correct this: 'blonde long hair' → '___ hair'",
          a: "long blonde",
          accept: { ignoreCase: true },
          successtip: "✓ FLIPPED!\nSize/length always before color - fixed! 📏🎨"
        },
        {
          type: "mc",
          q: "'She has a beautiful hair' - what's wrong?",
          options: [
            "Should be 'beautiful hair' (no 'a' - hair is uncountable)",
            "Nothing wrong",
            "Should be 'beautifully hair'"
          ],
          a: "Should be 'beautiful hair' (no 'a' - hair is uncountable)",
          successtip: "✓ ARTICLE ERROR!\nHair = uncountable mass. Only 'a hair' for single strand! 💇‍♀️"
        },
        {
          type: "order",
          q: "Fix and translate: 'brown curly beautiful cabello' → 'beautiful ___ hair'",
          a: "beautiful curly brown",
          successtip: "✓ REORDERED!\nOpinion → Shape → Color = natural English! 🔄"
        },
        {
          type: "mc",
          q: "Which is TOO informal/potentially rude?",
          options: [
            "'That old guy over there'",
            "'That elderly gentleman'",
            "'That senior man'"
          ],
          a: "'That old guy over there'",
          successtip: "✓ TONE AWARENESS!\n'Old guy' sounds dismissive. 'Elderly gentleman' = respectful! 🤵"
        },
        {
          type: "input",
          q: "'Red beautiful short hair' - correct order: '___'",
          a: "beautiful short red",
          accept: { ignoreCase: true },
          successtip: "✓ TRIPLE FIX!\nOpinion → Size → Color = all three rearranged! 🔧"
        },
        {
          type: "mc",
          q: "Error check: 'She's got gorgeous shoulder-length straight blonde hair.'",
          options: [
            "Perfect! All adjectives in correct order",
            "Too many adjectives",
            "Wrong order"
          ],
          a: "Perfect! All adjectives in correct order",
          tip: "✅ ANALYZING CORRECT SENTENCE!\n\nLet's break it down:\n• Gorgeous = OPINION ✅\n• Shoulder-length = SIZE ✅\n• Straight = SHAPE ✅\n• Blonde = COLOR ✅\n\nOrder: Opinion → Size → Shape → Color\n\nThis is PERFECT!\n\n4 adjectives is pushing it, but this one works because they're all different categories! 🎯",
          successtip: "✓ ALL CORRECT!\nThis is maximum adjectives you should use - any more = too much! 🌟"
        },
        {
          type: "mc",
          q: "'He's such a fat man' - more polite alternative:",
          options: [
            "'He's a heavyset man' or 'He's a large man'",
            "'He's a huge man'",
            "'He's an enormous man'"
          ],
          a: "'He's a heavyset man' or 'He's a large man'",
          successtip: "✓ RESPECTFUL!\n'Heavyset' and 'large' are neutral and polite - always choose kind words! 💙"
        },
        {
          type: "order",
          q: "Traduce (evitando errores): Ella tiene ojos marrones grandes y hermosos",
          a: "She has beautiful big brown eyes",
          successtip: "✓ PERFECT TRANSLATION!\nOpinion → Size → Color, and no article before 'eyes' (plural countable)! 👁️👁️"
        },
        {
          type: "mc",
          q: "Final error check: Which is COMPLETELY correct?",
          options: [
            "'My sister is a beautiful young woman with long straight black hair'",
            "'My sister is a young beautiful woman with straight long black hair'",
            "'My sister is a woman beautiful young with black straight long hair'"
          ],
          a: "'My sister is a beautiful young woman with long straight black hair'",
          successtip: "✓ FLAWLESS!\nOpinion → Age for person, Size → Shape → Color for hair = native-level! 🏆"
        }
      ]
    },

    // ========================================================================
    // SET 8: NATURAL DESCRIPTIONS - INTEGRATION
    // ========================================================================
    {
      id: "m7-u4-s8",
      title: "Set 8: Natural, Flowing Descriptions",
      questions: [
        {
          type: "mc",
          q: "Most natural way to describe someone you find attractive:",
          options: [
            "'She's really beautiful' or 'He's very handsome'",
            "'She's a beautiful attractive gorgeous woman'",
            "'He's got beautiful features and attractive face'"
          ],
          a: "'She's really beautiful' or 'He's very handsome'",
          successtip: "✓ SIMPLE = NATURAL!\nNatives keep it simple in casual conversation - don't over-describe! 💬✨"
        },
        {
          type: "input",
          q: "Complete naturally: 'My teacher is a ___ woman in her fifties.' (nice-looking/attractive older)",
          a: "nice-looking",
          accept: { ignoreCase: true, alts: ["attractive", "elegant"] },
          successtip: "✓ AGE-APPROPRIATE!\n'Nice-looking' is perfect for describing someone middle-aged or older respectfully! 😊"
        },
        {
          type: "mc",
          q: "Which sounds most like a native speaker?",
          options: [
            "'He's tall and has dark hair'",
            "'He is a tall man with dark-colored hair on his head'",
            "'He possesses tallness and dark hair coloration'"
          ],
          a: "'He's tall and has dark hair'",
          tip: "💬 NATURAL SPEECH!\n\nNative speakers:\n✅ Keep it simple and direct\n✅ Use contractions (He's, not He is)\n✅ Use 'and' to connect features\n\n❌ Don't over-explain ('dark-colored hair on his head' = obvious!)\n❌ Don't use overly formal words ('possesses tallness')\n\nSimple = natural = fluent! 🎯",
          successtip: "✓ CONVERSATIONAL!\nCasual descriptions use simple structure: 'Subject + be + adjective + and + have + noun' 💬"
        },
        {
          type: "order",
          q: "Traduce naturalmente: Mi vecino es un señor mayor muy amable",
          a: "My neighbor is a very nice elderly man",
          successtip: "✓ POLITE & SIMPLE!\n'Very nice elderly man' = respectful and natural! 🤝"
        },
        {
          type: "mc",
          q: "Complete: 'I met this guy at the party - ___, really cool.'",
          options: [
            "tall, brown hair",
            "a tall man with brown-colored hair",
            "tall person who possesses brown hair"
          ],
          a: "tall, brown hair",
          successtip: "✓ CASUAL STYLE!\nIn casual speech, natives use fragments and commas - sounds natural! 😎"
        },
        {
          type: "mc",
          q: "Describing your friend to help someone recognize them:",
          options: [
            "'She's about medium height with curly brown hair and glasses'",
            "'She is a woman who has medium height and her hair is brown and curly plus she wears glasses'",
            "'She possesses average stature, brown curly hair, and optical correction devices'"
          ],
          a: "'She's about medium height with curly brown hair and glasses'",
          successtip: "✓ PRACTICAL!\nReal situations = concise, clear descriptions with key features! 👓"
        },
        {
          type: "input",
          q: "Natural response: 'What does she look like?' → 'She's ___ and ___.' (pretty / has long hair)",
          a: "pretty, has long hair",
          accept: { ignoreCase: true, alts: ["pretty, she has long hair", "pretty and has long hair"] },
          successtip: "✓ QUESTION-ANSWER FLOW!\n'What does X look like?' = asking for physical description - answer with features! 👤"
        },
        {
          type: "mc",
          q: "Which uses vocabulary + adjective order NATURALLY together?",
          options: [
            "'My boss is a nice middle-aged man with short gray hair'",
            "'My boss is a middle-aged nice man with gray short hair'",
            "'My boss is a man with hair that is short and gray and he is middle-aged and nice'"
          ],
          a: "'My boss is a nice middle-aged man with short gray hair'",
          successtip: "✓ INTEGRATED!\nOpinion → Age for person, Size → Color for hair - plus natural flow! 💼"
        },
        {
          type: "order",
          q: "Traduce: ¿Cómo luce tu hermana? Es alta con cabello rubio largo",
          a: "What does your sister look like? She's tall with long blonde hair",
          successtip: "✓ NATURAL Q&A!\nQuestion format + descriptive answer = real conversation! 💬"
        },
        {
          type: "mc",
          q: "Complete naturally: 'You'll recognize him easily - he's the ___ guy with ___.'",
          options: [
            "tall / glasses and a beard",
            "tall handsome attractive / facial hair and optical devices",
            "big tall large / glass things on face and chin hair"
          ],
          a: "tall / glasses and a beard",
          successtip: "✓ DISTINCTIVE FEATURES!\nFor recognition: mention 1-2 key features that stand out! 👓🧔"
        }
      ]
    },

    // ========================================================================
    // SET 9: REAL-WORLD INTEGRATION
    // ========================================================================
    {
      id: "m7-u4-s9",
      title: "Set 9: Putting It All Together",
      questions: [
        {
          type: "mc",
          q: "You're helping police describe someone. Most useful description:",
          options: [
            "'Male, about 6 feet tall, athletic build, short dark hair, late twenties'",
            "'A very attractive and handsome young man'",
            "'Someone with features'"
          ],
          a: "'Male, about 6 feet tall, athletic build, short dark hair, late twenties'",
          successtip: "✓ PRACTICAL!\nSpecifics matter: height, build, hair, age = identifying details! 👮‍♂️"
        },
        {
          type: "input",
          q: "Dating app description: 'I'm ___, love sports, and have ___.' (athletic build / short brown hair)",
          a: "athletic build, short brown hair",
          accept: { ignoreCase: true, alts: ["athletic, short brown hair"] },
          successtip: "✓ MODERN CONTEXT!\nOnline profiles: mention build, hobbies, distinctive features! 📱💕"
        },
        {
          type: "mc",
          q: "Describing a lost child to security: Which is MOST helpful?",
          options: [
            "'About 4 years old, wearing a red shirt, curly blonde hair, very small'",
            "'A cute little kid'",
            "'Young child with hair'"
          ],
          a: "'About 4 years old, wearing a red shirt, curly blonde hair, very small'",
          tip: "🚨 EMERGENCY DESCRIPTION!\n\nImportant details:\n✅ Age (approximate)\n✅ Clothing (immediately visible)\n✅ Hair (distinctive)\n✅ Size/height (relative)\n\nVague descriptions don't help:\n❌ 'Cute kid' (not useful)\n❌ 'With hair' (everyone has features)\n\nIn emergencies: BE SPECIFIC! 🎯",
          successtip: "✓ CRITICAL INFO!\nClothing is KEY in lost child situations - most visible from distance! 👕"
        },
        {
          type: "order",
          q: "Traduce: Estoy buscando a una mujer alta con cabello rojo corto",
          a: "I'm looking for a tall woman with short red hair",
          successtip: "✓ SEARCH DESCRIPTION!\nSize (tall) + Size (short) + Color (red) = clear identifier! 🔍"
        },
        {
          type: "mc",
          q: "Meeting someone from online: 'How will I recognize you?' Best response:",
          options: [
            "'I'll be wearing a blue jacket and I'm about 5'8\" with glasses'",
            "'I look like a normal person'",
            "'I have a face and hair'"
          ],
          a: "'I'll be wearing a blue jacket and I'm about 5'8\" with glasses'",
          successtip: "✓ MEETUP INFO!\nClothing + one permanent feature (height/glasses/hair) = easy recognition! 👓🧥"
        },
        {
          type: "mc",
          q: "Describing actor for casting: Which uses vocabulary + order correctly?",
          options: [
            "'We need a handsome tall man in his thirties with short dark hair'",
            "'We need a tall handsome man in his thirties with dark short hair'",
            "'We need a man tall and handsome with thirties age and dark hair short'"
          ],
          a: "'We need a handsome tall man in his thirties with short dark hair'",
          successtip: "✓ PROFESSIONAL DESCRIPTION!\nOpinion → Size → Age, then Size → Color for hair = casting call perfect! 🎬"
        },
        {
          type: "input",
          q: "Witness statement: 'The suspect was ___ with ___ and ___.' (male / medium build / bald)",
          a: "male, medium build, bald",
          accept: { ignoreCase: true, alts: ["male / medium build / bald"] },
          successtip: "✓ OFFICIAL STATEMENT!\nGender, build, distinctive features (bald) = police report essentials! 👮"
        },
        {
          type: "mc",
          q: "Which description is too vague to be useful?",
          options: [
            "'A person with a face'",
            "'A woman with shoulder-length blonde hair'",
            "'A tall man with a beard'"
          ],
          a: "'A person with a face'",
          successtip: "✓ OBVIOUSLY USELESS!\nEveryone has basic features - mention DISTINCTIVE characteristics! 🎯"
        },
        {
          type: "order",
          q: "Traduce: El sospechoso es un hombre joven y delgado con cabello negro corto",
          a: "The suspect is a young slim man with short black hair",
          successtip: "✓ OFFICIAL DESCRIPTION!\nAge → Shape for person, Size → Color for hair = professional report! 📋"
        },
        {
          type: "mc",
          q: "Complete photo caption: 'Me and my ___ at graduation!' (gorgeous young sister / young gorgeous sister)",
          options: [
            "gorgeous young sister",
            "young gorgeous sister",
            "sister young gorgeous"
          ],
          a: "gorgeous young sister",
          successtip: "✓ SOCIAL MEDIA!\nOpinion (gorgeous) → Age (young) = natural caption with affection! 📸❤️"
        }
      ]
    },

    // ========================================================================
    // SET 10: MASTERY - COMPLETE DESCRIPTIONS
    // ========================================================================
    {
      id: "m7-u4-s10",
      title: "Set 10: Complete Description Mastery",
      questions: [
        {
          type: "mc",
          q: "Which is a COMPLETE, NATURAL, CORRECT description?",
          options: [
            "'My girlfriend is a beautiful young woman with long straight brown hair and green eyes'",
            "'My girlfriend is a young beautiful woman with straight long brown hair and eyes green'",
            "'My girlfriend is woman beautiful young who has hair brown straight long plus eyes that are green'"
          ],
          a: "'My girlfriend is a beautiful young woman with long straight brown hair and green eyes'",
          tip: "🌟 COMPLETE MASTERY!\n\nThis sentence has EVERYTHING:\n\n✅ Opinion → Age for person (beautiful young)\n✅ Size → Shape → Color for hair (long straight brown)\n✅ Color for eyes (green)\n✅ Natural flowing structure\n✅ Appropriate amount of detail (not too much!)\n\nThis is exactly how natives describe people they love! 💕\n\nAll Unit 4 concepts integrated perfectly! 🎯",
          successtip: "✓ PERFECT DESCRIPTION!\nBody vocabulary + adjective order + natural flow = native-level mastery! 🏆"
        },
        {
          type: "input",
          q: "Complete description: 'My dad is a ___ man with ___ and ___.' (nice-looking middle-aged / short gray hair / a beard)",
          a: "nice-looking middle-aged, short gray hair, a beard",
          accept: { ignoreCase: true, alts: ["nice-looking middle-aged / short gray hair / a beard"] },
          successtip: "✓ FAMILY DESCRIPTION!\nOpinion → Age for person, Size → Color for hair, plus distinctive feature! 👨‍🦳"
        },
        {
          type: "mc",
          q: "Describing best friend to your date: Which sounds MOST natural?",
          options: [
            "'My best friend Alex is really tall and athletic with short blonde hair - you'll like him!'",
            "'My best friend Alex is a tall athletic man with hair that is short and blonde in color - I predict you will have positive feelings about him!'",
            "'My best friend Alex possesses great height and athletic physique with capillary characteristics of shortness and blonde coloration'"
          ],
          a: "'My best friend Alex is really tall and athletic with short blonde hair - you'll like him!'",
          successtip: "✓ CONVERSATIONAL GOLD!\nCasual, warm, natural + correct order = how friends actually talk! 💬😊"
        },
        {
          type: "order",
          q: "Traduce completamente: Mi profesora es una mujer mayor elegante con cabello gris corto",
          a: "My teacher is an elegant elderly woman with short gray hair",
          successtip: "✓ RESPECTFUL DESCRIPTION!\nOpinion (elegant) → Age (elderly) + Size (short) → Color (gray) = perfect! 👩‍🏫"
        },
        {
          type: "mc",
          q: "Instagram caption for selfie with friend: Choose the natural one:",
          options: [
            "'Beach day with this gorgeous tall brunette! 🌊☀️'",
            "'Beach day with this tall gorgeous brunette person! 🌊☀️'",
            "'Beach day with this brunette who is tall and gorgeous! 🌊☀️'"
          ],
          a: "'Beach day with this gorgeous tall brunette! 🌊☀️'",
          successtip: "✓ SOCIAL MEDIA STYLE!\nOpinion → Size, plus casual language = perfect caption! 📱✨"
        },
        {
          type: "mc",
          q: "Which correctly uses ALL the vocabulary + grammar from Unit 4?",
          options: [
            "'She's a beautiful young woman with gorgeous curly brown hair, big brown eyes, and high cheekbones'",
            "'She's a young beautiful woman with curly gorgeous brown hair, brown big eyes, and cheekbones high'",
            "'She's woman young beautiful who has hair brown curly gorgeous, eyes big and brown, plus cheekbones that are high'"
          ],
          a: "'She's a beautiful young woman with gorgeous curly brown hair, big brown eyes, and high cheekbones'",
          successtip: "✓ COMPREHENSIVE!\nFace vocab (cheekbones, eyes) + adjective order + natural structure = Unit 4 complete! 🎓"
        },
        {
          type: "input",
          q: "Write a complete description: 'He's ___.' (Include: handsome, tall, middle-aged, short dark hair)",
          a: "a handsome tall middle-aged man with short dark hair",
          accept: { ignoreCase: true, alts: ["handsome, tall, middle-aged with short dark hair", "a handsome tall middle-aged guy with short dark hair"] },
          successtip: "✓ FULL DESCRIPTION!\nAll elements in correct order = you can now describe anyone completely! 💯"
        },
        {
          type: "mc",
          q: "Meeting future in-laws, describing your partner to them: Most appropriate:",
          options: [
            "'She's lovely, about medium height, with beautiful long dark hair'",
            "'She's super hot with amazing hair!'",
            "'She possesses adequate height and capillary excellence'"
          ],
          a: "'She's lovely, about medium height, with beautiful long dark hair'",
          successtip: "✓ APPROPRIATE REGISTER!\nFormal situation = respectful adjectives, clear description, warm tone! 💍😊"
        },
        {
          type: "order",
          q: "Traduce: El actor principal es un hombre alto, apuesto y joven con cabello castaño ondulado",
          a: "The lead actor is a handsome tall young man with wavy brown hair",
          successtip: "✓ FILM DESCRIPTION!\nOpinion → Size → Age + Shape → Color = professional movie review language! 🎬"
        },
        {
          type: "mc",
          q: "FINAL MASTERY QUESTION: You've learned body vocabulary, adjective order, and natural descriptions. Which sentence shows COMPLETE mastery of ALL Unit 4 concepts?",
          options: [
            "'My sister is a gorgeous young woman with beautiful long curly red hair, green eyes, high cheekbones, and an athletic build - everyone says she looks like a model!'",
            "'My sister is a young gorgeous woman with curly long beautiful red hair, eyes green, cheekbones high, and build athletic - everyone says she model looks like!'",
            "'My sister is woman who young and gorgeous with hair that long curly red beautiful, with eyes being green and cheekbones high plus athletic in terms of build'"
          ],
          a: "'My sister is a gorgeous young woman with beautiful long curly red hair, green eyes, high cheekbones, and an athletic build - everyone says she looks like a model!'",
          tip: "🎓 UNIT 4 COMPLETE MASTERY!\n\nThis sentence demonstrates:\n\n✅ Body vocabulary:\n• Cheekbones, eyes, build\n\n✅ Adjective order (MULTIPLE!):\n• 'Gorgeous young' (Opinion-Age)\n• 'Beautiful long curly red' (Opinion-Size-Shape-Color)\n• 'Athletic build' (Shape-Noun)\n\n✅ Natural structure:\n• Conversational flow\n• Appropriate detail\n• Real-world context\n\n✅ Hair/appearance slang:\n• 'Red hair' (natural way to say ginger)\n• 'Looks like a model' (compliment)\n\nAll Unit 4 concepts perfectly integrated! 🌟",
          successtip: "🎉 UNIT 4 COMPLETELY MASTERED! 🎉\n\n✅ BODY VOCABULARY\n• Face: chin, eyebrows, cheekbones, nostrils ✅\n• Build: slim, athletic, heavyset, medium ✅\n• Hair: straight, wavy, curly, bald, length ✅\n• Age: young, middle-aged, elderly ✅\n\n✅ ADJECTIVE ORDER\n1. Opinion (beautiful, handsome) ✅\n2. Size (tall, short, long) ✅\n3. Shape (curly, slim, round) ✅\n4. Age (young, old, middle-aged) ✅\n5. Color (brown, blonde, red) ✅\n\n✅ NATURAL DESCRIPTIONS\n• 2-3 adjectives maximum ✅\n• Conversational flow ✅\n• Context-appropriate ✅\n\n✅ RESPECTFUL LANGUAGE\n• Polite terms for age/size ✅\n• Appropriate slang (ginger, silver fox) ✅\n• Cultural awareness ✅\n\n🌟 YOU CAN NOW:\n• Describe anyone completely ✅\n• Use adjectives in perfect order ✅\n• Sound natural and fluent ✅\n• Be respectful and appropriate ✅\n\nB1-B2 description mastery complete! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - MEETING NEW NEIGHBORS (5 PASSAGES OF ~40 WORDS EACH)
  // ==========================================================================
  readingSet: {
    id: "m7-u4-reading",
    title: "Reading Practice - Meeting New Neighbors",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: First Impressions (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Meeting New Neighbors**

**First Impressions**

Last week, a new family moved in next door. I first met Sarah, a beautiful young woman with gorgeous long blonde hair. She was very friendly and introduced me to her husband, Mike, a handsome tall guy with short brown hair and a nice smile.`
      },
      {
        type: "mc",
        q: "In 'a beautiful young woman,' what adjective order is used?",
        options: [
          "Opinion then Age (beautiful → young)",
          "Age then Opinion",
          "No specific order"
        ],
        a: "Opinion then Age (beautiful → young)",
        successtip: "✓ ORDER SPOTTED!\nOpinion (beautiful) before Age (young) = natural English! 📐"
      },
      {
        type: "input",
        q: "How is Sarah's hair described? (three adjectives)",
        a: "gorgeous long blonde",
        accept: { ignoreCase: true, alts: ["long blonde", "gorgeous blonde"] },
        successtip: "✓ TRIPLE DESCRIPTION!\nOpinion (gorgeous) → Size (long) → Color (blonde) = perfect order! 💇‍♀️✨"
      },

      // ======================================================================
      // BLOQUE 2: The Kids (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**The Children**

They have two adorable kids. Emma, their daughter, is about seven with beautiful curly red hair - a real little ginger! Their son Jake is nine, tall for his age, with straight dark hair and big brown eyes. Both kids are very polite.`
      },
      {
        type: "mc",
        q: "The text mentions Emma has 'beautiful curly red hair.' Is this correct adjective order?",
        options: [
          "Yes - Opinion, Shape, Color",
          "No - should be 'curly beautiful red'",
          "No - should be 'red curly beautiful'"
        ],
        a: "Yes - Opinion, Shape, Color",
        successtip: "✓ CORRECT!\nOpinion (beautiful) → Shape (curly) → Color (red) = textbook order! 🎯"
      },
      {
        type: "mc",
        q: "'A real little ginger' refers to:",
        options: [
          "A child with red/orange hair (British slang)",
          "A cooking ingredient",
          "A short person"
        ],
        a: "A child with red/orange hair (British slang)",
        successtip: "✓ SLANG CAUGHT!\n'Ginger' = friendly British term for redhead - context makes it affectionate! 🦊🇬🇧"
      },

      // ======================================================================
      // BLOQUE 3: The Grandparents (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Meeting Grandma**

Sarah's mother also lives with them. She's an elegant elderly woman with short gray hair and beautiful blue eyes. Despite being in her seventies, she looks young for her age - a real silver fox! She's incredibly kind and energetic.`
      },
      {
        type: "mc",
        q: "'An elegant elderly woman' uses which adjective order?",
        options: [
          "Opinion then Age",
          "Age then Opinion",
          "Random order"
        ],
        a: "Opinion then Age",
        successtip: "✓ PATTERN AGAIN!\nOpinion (elegant) always before Age (elderly) = consistent rule! 👵✨"
      },
      {
        type: "input",
        q: "What slang term describes an attractive older person with gray hair?",
        a: "silver fox",
        accept: { ignoreCase: true },
        successtip: "✓ COMPLIMENT SLANG!\n'Silver fox' = very flattering term for elegant older people! Usually men, but works for women too! 🦊💫"
      },

      // ======================================================================
      // BLOQUE 4: Physical Descriptions (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Getting to Know Them**

Mike works out regularly - he has an athletic build with broad shoulders. Sarah is of medium height and slim, probably from running every morning. The kids clearly get their looks from their parents - strong chins and high cheekbones run in the family!`
      },
      {
        type: "mc",
        q: "Which vocabulary from Unit 4 appears in this passage?",
        options: [
          "All of these: athletic build, medium height, slim, chin, cheekbones",
          "Only height descriptions",
          "None - it's all new vocabulary"
        ],
        a: "All of these: athletic build, medium height, slim, chin, cheekbones",
        successtip: "✓ VOCABULARY IN CONTEXT!\nBody parts (chin, cheekbones) + build terms (athletic, slim) = Unit 4 integrated! 💪"
      },
      {
        type: "mc",
        q: "'Broad shoulders' means:",
        options: [
          "Wide, strong-looking shoulders",
          "Narrow shoulders",
          "Shoulders that are too big"
        ],
        a: "Wide, strong-looking shoulders",
        successtip: "✓ PHYSICAL FEATURE!\n'Broad' = wide and strong (positive description for men especially)! 💪"
      },

      // ======================================================================
      // BLOQUE 5: Reflection + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Great Neighbors**

They're wonderful neighbors. From the gorgeous young woman and handsome tall man to the cute ginger kid and elegant silver fox grandma, every person brings something special. It's nice having a beautiful friendly family next door. Best neighbors ever!`
      },
      {
        type: "mc",
        q: "This passage uses multiple descriptions with correct adjective order. How many can you spot?",
        options: [
          "Four correct combinations: gorgeous young / handsome tall / cute ginger / elegant silver fox",
          "Only one or two",
          "None are in correct order"
        ],
        a: "Four correct combinations: gorgeous young / handsome tall / cute ginger / elegant silver fox",
        successtip: "✓ ALL CORRECT!\nEvery description follows Opinion → Age/Size pattern = mastery! 🌟"
      },
            {
        type: "mc",
        q: "Throughout the reading, adjective order was consistently used. What pattern was most common?",
        options: [
          "Opinion before Age/Size",
          "Color before everything else",
          "No consistent pattern"
        ],
        a: "Opinion before Age/Size",
        tip: "📐 READING PATTERN ANALYSIS!\n\nExamples from text:\n• 'Beautiful young woman' (Opinion-Age)\n• 'Handsome tall guy' (Opinion-Size)\n• 'Elegant elderly woman' (Opinion-Age)\n• 'Beautiful curly red hair' (Opinion-Shape-Color)\n\nOPINION always came FIRST!\n\nThis is the #1 rule natives follow automatically!\n\nNow you can recognize it in real English! 🎯",
        successtip: "✓ PATTERN RECOGNIZED!\nOpinion first appears in EVERY description - this is how natives write! 📖✨"
      },
      {
        type: "mc",
        q: "FINAL QUESTION: This reading integrated vocabulary (body parts, hair, build) with grammar (adjective order) naturally. What did you notice?",
        options: [
          "All descriptions followed Opinion → Size/Shape → Age → Color pattern naturally",
          "The order was random throughout",
          "Only vocabulary was used, no grammar patterns"
        ],
        a: "All descriptions followed Opinion → Size/Shape → Age → Color pattern naturally",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ BODY VOCABULARY\n• Face: chin, cheekbones, eyes ✅\n• Hair: curly, straight, blonde, red, gray ✅\n• Build: athletic, slim, broad shoulders ✅\n• Height: tall, medium height ✅\n\n✅ ADJECTIVE ORDER (Every time!)\n• Beautiful young woman ✅\n• Gorgeous long blonde hair ✅\n• Handsome tall guy ✅\n• Beautiful curly red hair ✅\n• Elegant elderly woman ✅\n• Short gray hair ✅\n• Athletic build ✅\n\n✅ RESPECTFUL SLANG\n• 'Little ginger' (affectionate) ✅\n• 'Silver fox' (compliment) ✅\n\n✅ NATURAL DESCRIPTIONS\n• Family context ✅\n• Real-world situations ✅\n• Conversational tone ✅\n\n🌟 ALL UNIT 4 CONCEPTS IN ACTION!\nVocabulary + Grammar = Natural English! 💪📖"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 4 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u4-review",
    title: "⭐ Unit 4 Review: Describing People",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which sentence uses CORRECT adjective order?",
        options: [
          "She has beautiful long curly brown hair",
          "She has long beautiful curly brown hair",
          "She has brown curly long beautiful hair"
        ],
        a: "She has beautiful long curly brown hair",
        tip: "📐 REVIEW: ADJECTIVE ORDER!\n\nThe complete order:\n1. OPINION (beautiful)\n2. SIZE/LENGTH (long)\n3. SHAPE (curly)\n4. COLOR (brown)\n\n✅ Beautiful long curly brown = correct!\n\nOpinion ALWAYS comes first!\nColor ALWAYS comes last!\n\nThis is Unit 4's core grammar! 🎯",
        successtip: "✓ PERFECT ORDER!\nMemory trick: 'Oscar Stays Strong After Coffee' = Opinion, Size, Shape, Age, Color! 📝"
      },
      
      {
        type: "input",
        q: "The two openings in your nose are called your ___.",
        a: "nostrils",
        accept: { ignoreCase: true },
        successtip: "✓ FACE VOCABULARY!\nRelated: 'nostril flare' = sign of anger or heavy breathing in body language! 😤"
      },
      
      {
        type: "mc",
        q: "Most POLITE way to describe a large person:",
        options: [
          "Heavyset or large build",
          "Fat",
          "Huge"
        ],
        a: "Heavyset or large build",
        successtip: "✓ RESPECTFUL LANGUAGE!\nBonus polite terms: 'plus-size', 'big-boned', 'sturdy build' - always choose kind words! 💙"
      },
      
      {
        type: "order",
        q: "Traduce: Él es un hombre joven y apuesto con cabello negro corto",
        a: "He is a handsome young man with short black hair",
        successtip: "✓ DOUBLE ORDER!\nOpinion-Age for person (handsome young) + Size-Color for hair (short black) = both patterns! 🎯"
      },
      
      {
        type: "mc",
        q: "British slang for someone with red/orange hair:",
        options: [
          "Ginger",
          "Blondie",
          "Brunette"
        ],
        a: "Ginger",
        successtip: "✓ UK SLANG!\nFun fact: 'Ginger' comes from the spice color. Famous gingers: Ed Sheeran, Prince Harry! 🇬🇧🦊"
      },
      
      {
        type: "input",
        q: "Fix the error: 'She has a beautiful hair' → 'She has ___ hair'",
        a: "beautiful",
        accept: { ignoreCase: true },
        successtip: "✓ CORRECTED!\nReminder: Hair is uncountable (like water, rice) - no 'a' unless it's a single strand! 💇‍♀️"
      },
      
      {
        type: "mc",
        q: "An attractive older man with gray hair is called:",
        options: [
          "A silver fox (complimentary slang)",
          "A gray mouse",
          "An old timer"
        ],
        a: "A silver fox (complimentary slang)",
        successtip: "✓ COMPLIMENT!\nCelebrity silver foxes: George Clooney, Anderson Cooper, Richard Gere = total foxes! 🦊✨"
      },
      
      {
        type: "mc",
        q: "'Tall young athletic' or 'athletic tall young' or 'young athletic tall'?",
        options: [
          "Athletic tall young (Opinion-Size-Age)",
          "Tall young athletic",
          "Young athletic tall"
        ],
        a: "Athletic tall young (Opinion-Size-Age)",
        tip: "📐 COMPLEX ORDER!\n\nBreaking it down:\n• Athletic = OPINION (implies fit/good)\n• Tall = SIZE\n• Young = AGE\n\nOrder: Opinion → Size → Age\n= Athletic tall young\n\nBUT remember: 3 adjectives is maximum!\nBetter to say: 'a tall athletic young man'\n\nDon't overload descriptions! 🎯",
        successtip: "✓ TRICKY!\nNatives would usually simplify to 'tall and athletic' or 'a young athlete' - natural is better! 😊"
      },
      
      {
        type: "order",
        q: "Traduce completamente: Mi abuela es una señora mayor elegante con cabello blanco corto",
        a: "My grandmother is an elegant elderly woman with short white hair",
        successtip: "✓ RESPECTFUL DESCRIPTION!\nBonus vocabulary: 'white hair' or 'silver hair' (both natural for very elderly) = more poetic than 'gray'! 👵✨"
      },
      
      {
        type: "mc",
        q: "FINAL MASTERY: Which sentence demonstrates COMPLETE Unit 4 mastery?",
        options: [
          "'My teacher is a nice middle-aged man with short gray hair, high cheekbones, and an athletic build'",
          "'My teacher is a middle-aged nice man with gray short hair, cheekbones high, and build athletic'",
          "'My teacher is man nice and middle-aged who has hair that gray and short plus cheekbones and athletic'"
        ],
        a: "'My teacher is a nice middle-aged man with short gray hair, high cheekbones, and an athletic build'",
        tip: "🎓 COMPLETE UNIT 4 INTEGRATION!\n\nThis sentence has EVERYTHING:\n\n✅ Adjective order:\n• 'Nice middle-aged' (Opinion-Age) ✅\n• 'Short gray hair' (Size-Color) ✅\n• 'High cheekbones' (Size-Noun) ✅\n• 'Athletic build' (Opinion-Noun) ✅\n\n✅ Body vocabulary:\n• Hair, cheekbones, build ✅\n\n✅ Natural structure:\n• Clear, conversational ✅\n• Appropriate detail ✅\n• Professional context ✅\n\n✅ Age description:\n• 'Middle-aged' (respectful) ✅\n\nAll concepts perfectly integrated! 🌟",
        successtip: "🎉 UNIT 4 COMPLETELY MASTERED! 🎉\n\n✅ VOCABULARY MASTERY\n• Face: chin, eyebrows, cheekbones, nostrils, forehead ✅\n• Hair: straight, wavy, curly, bald, lengths, colors ✅\n• Build: slim, athletic, heavyset, medium ✅\n• Height: tall, short, medium ✅\n• Age: young, middle-aged, elderly ✅\n\n✅ ADJECTIVE ORDER\n1. Opinion (beautiful, handsome) ✅\n2. Size (tall, long, short) ✅\n3. Shape (curly, slim, round) ✅\n4. Age (young, old, middle-aged) ✅\n5. Color (brown, blonde, black, red) ✅\n\n✅ NATURAL DESCRIPTIONS\n• Maximum 2-3 adjectives ✅\n• Conversational flow ✅\n• Context-appropriate register ✅\n\n✅ RESPECTFUL LANGUAGE\n• Polite age terms (elderly, senior) ✅\n• Polite size terms (heavyset, large) ✅\n• Friendly slang (ginger, silver fox) ✅\n\n✅ REAL-WORLD USAGE\n• Dating profiles ✅\n• Police descriptions ✅\n• Meeting people ✅\n• Family descriptions ✅\n\n🌟 YOU CAN NOW:\n• Describe anyone completely and naturally ✅\n• Use perfect adjective order automatically ✅\n• Choose respectful, appropriate words ✅\n• Sound like a native speaker ✅\n\nB1-B2 description skills = MASTERED! 💪\n\n¡Excelente trabajo! Ready for Unit 5! 🚀"
      }
    ]
  }
};
